// Effects
import { all, call, put, takeLatest } from "redux-saga/effects";

// Service
import { defaultApi } from "../../utils/axiosApi";

// Constants
import {
  GET_SUBSCRIPTION_PLAN,
  GET_SUBSCRIPTION_PLAN_SUCCESS,
  GET_SUBSCRIPTION_PLAN_FAILURE,
  UPDATE_SUBSCRIPTION_PLAN_SUCCESS,
  UPDATE_SUBSCRIPTION_PLAN_FAILURE,
  UPDATE_SUBSCRIPTION_PLAN,
  PAUSE_SUBSCRIPTION_SUCCESS,
  PAUSE_SUBSCRIPTION_FAILURE,
  PAUSE_SUBSCRIPTION,
  SUBSCRIPTION_FEEDBACK_REQUEST,
  SUBSCRIPTION_FEEDBACK_SUCCESS,
  SUBSCRIPTION_FEEDBACK_FAILURE,
  CREATE_BILLING_CHECKOUT,
  CREATE_BILLING_CHECKOUT_SUCCESS,
  CREATE_BILLING_CHECKOUT_FAILURE,
  APPLY_BILLING_PLAN_CHANGE,
  APPLY_BILLING_PLAN_CHANGE_SUCCESS,
  APPLY_BILLING_PLAN_CHANGE_FAILURE,
  OPEN_BILLING_MANAGEMENT,
  OPEN_BILLING_MANAGEMENT_SUCCESS,
  OPEN_BILLING_MANAGEMENT_FAILURE,
  GET_BILLING_PLANS,
  GET_BILLING_PLANS_SUCCESS,
  GET_BILLING_PLANS_FAILURE,
} from "../../utils/constants";
import { createNotification } from "../../utils/notificationManager";
import { openPaddleBillingCheckout } from "../../utils/paddleBilling";
import {
  getBillingCatalogErrorMessage,
  validateBillingPlans,
} from "./billingCatalog";
import { getCustomerPortalUrl } from "./subscriptionPresentation";

export function getErrorMessage(error, fallback) {
  const backendMessage =
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    error?.response?.data?.error?.detail ||
    error?.response?.data?.error;
  const status = error?.response?.status;

  if (backendMessage) {
    return backendMessage;
  }

  if (status === 404) {
    return "Subscription not found. Refresh the page and try again.";
  }

  if (status === 502) {
    return "Billing provider is unavailable right now. Please try again in a moment.";
  }

  if (status === 400) {
    return "Please review your billing change and try again.";
  }

  return (
    error?.message ||
    fallback
  );
}

export function* getSubscriptionPlanRequest({ payload }) {
  try {
    let url = `subscription/get-subscription-details/`;

    if (payload?.refetch || payload === true) {
      url = `subscription/get-subscription-details/?refetch=true`;
    }

    const response = yield call(defaultApi, url, "GET");
    yield put({
      type: GET_SUBSCRIPTION_PLAN_SUCCESS,
      payload: { data: response },
    });
  } catch (err) {
    yield put({
      type: GET_SUBSCRIPTION_PLAN_FAILURE,
      payload: err,
    });
  }
}

export function* getBillingPlansRequest() {
  try {
    const response = yield call(
      defaultApi,
      "subscription/billing/plans/",
      "GET"
    );
    const billingPlans = validateBillingPlans(response?.data);

    yield put({
      type: GET_BILLING_PLANS_SUCCESS,
      payload: { data: billingPlans },
    });
  } catch (error) {
    yield put({
      type: GET_BILLING_PLANS_FAILURE,
      payload: {
        error,
        message: getBillingCatalogErrorMessage(error),
      },
    });
  }
}

export function* upgradeSubscriptionPlanRequest({ payload }) {
  try {
    const response = yield call(
      defaultApi,
      "subscription/upgrade-subscription/",
      "POST",
      payload
    );
    yield put({
      type: UPDATE_SUBSCRIPTION_PLAN_SUCCESS,
      payload: { data: response },
    });
  } catch (err) {
    yield put({
      type: UPDATE_SUBSCRIPTION_PLAN_FAILURE,
      payload: err,
    });
  }
}

export function* handlePauseSubscriptionRequest({ payload }) {
  try {
    const response = yield call(
      defaultApi,
      "subscription/pause-subscription/",
      "POST",
      payload
    );
    yield put({
      type: PAUSE_SUBSCRIPTION_SUCCESS,
      payload: { data: response },
    });
  } catch (err) {
    yield put({
      type: PAUSE_SUBSCRIPTION_FAILURE,
      payload: err,
    });
  }
}

export function* handleSubscriptionFeedbackRequest({ payload }) {
  try {
    const response = yield call(
      defaultApi,
      "subscription/feedback/",
      "POST",
      payload
    );
    yield put({
      type: SUBSCRIPTION_FEEDBACK_SUCCESS,
      payload: { data: response },
    });
  } catch (err) {
    yield put({
      type: SUBSCRIPTION_FEEDBACK_FAILURE,
      payload: err,
    });
  }
}

export function* createBillingCheckoutRequest({ payload }) {
  try {
    const priceId = payload?.price_id;

    if (!priceId) {
      throw new Error("Missing Paddle Billing price ID");
    }

    const requestBody = { price_id: priceId };

    if (!localStorage.getItem("access_token") && payload?.email) {
      requestBody.email = payload.email;
    }

    const response = yield call(
      defaultApi,
      "subscription/billing/checkout/",
      "POST",
      requestBody
    );
    const checkoutData = response?.data || response;

    yield put({
      type: CREATE_BILLING_CHECKOUT_SUCCESS,
      payload: { data: response },
    });
    yield call(openPaddleBillingCheckout, checkoutData);
  } catch (err) {
    const message = getErrorMessage(
      err,
      "Unable to start Paddle Billing checkout"
    );

    yield call(createNotification, "warning", message, 3000);
    yield put({
      type: CREATE_BILLING_CHECKOUT_FAILURE,
      payload: err,
    });
  }
}

function* refreshBillingSubscriptionDetails() {
  yield put({
    type: GET_SUBSCRIPTION_PLAN,
    payload: { refetch: true, silent: true },
  });
}

export function* applyBillingPlanChangeRequest({ payload }) {
  try {
    const response = yield call(
      defaultApi,
      "subscription/billing/change-plan/",
      "POST",
      {
        new_price_id: payload?.new_price_id,
        proration_billing_mode:
          payload?.proration_billing_mode || "prorated_immediately",
        ...(payload?.on_payment_failure
          ? { on_payment_failure: payload.on_payment_failure }
          : {}),
      }
    );

    yield put({
      type: APPLY_BILLING_PLAN_CHANGE_SUCCESS,
      payload: { data: response },
    });
    yield call(
      createNotification,
      "success",
      "Subscription plan updated successfully.",
      3000
    );
    yield* refreshBillingSubscriptionDetails();
  } catch (err) {
    const message = getErrorMessage(
      err,
      "Unable to update the billing plan."
    );

    yield call(createNotification, "warning", message, 3000);
    yield put({
      type: APPLY_BILLING_PLAN_CHANGE_FAILURE,
      payload: err,
    });
  }
}

function getManagementUrl(subscription, type) {
  const paymentInformation = subscription?.payment_information || {};
  const rawSubscription = paymentInformation.raw_subscription || {};
  const managementUrls = rawSubscription.management_urls || {};
  const manageUrl =
    subscription?.manage_url ||
    subscription?.portal_url ||
    paymentInformation.manage_url ||
    paymentInformation.portal_url ||
    managementUrls.manage ||
    managementUrls.portal ||
    "";

  if (type === "manage") {
    if (manageUrl) {
      return getCustomerPortalUrl(manageUrl);
    }

    return getCustomerPortalUrl(
      subscription?.cancel_url ||
        paymentInformation.cancel_url ||
        managementUrls.cancel ||
        "",
      true
    );
  }

  if (type === "update") {
    return (
      subscription?.update_url ||
      paymentInformation.update_url ||
      managementUrls.update_payment_method ||
      managementUrls.updatePaymentMethod ||
      ""
    );
  }

  return (
    subscription?.cancel_url ||
    paymentInformation.cancel_url ||
    managementUrls.cancel ||
    ""
  );
}

export function* openBillingManagementRequest({ payload }) {
  try {
    const response = yield call(
      defaultApi,
      "subscription/get-subscription-details/?refetch=true",
      "GET"
    );
    const subscription = response?.data?.data || response?.data || response;
    const managementUrl = getManagementUrl(subscription, payload?.type);

    if (!managementUrl) {
      throw new Error(
        "Subscription management link is not available. Please refresh and try again."
      );
    }

    yield put({
      type: OPEN_BILLING_MANAGEMENT_SUCCESS,
      payload: { data: response },
    });
    yield call([window, window.open], managementUrl, "_blank", "noopener,noreferrer");
  } catch (err) {
    const message = getErrorMessage(
      err,
      "Unable to open subscription management. Please refresh and try again."
    );

    yield call(createNotification, "warning", message, 3000);
    yield put({
      type: OPEN_BILLING_MANAGEMENT_FAILURE,
      payload: err,
    });
  }
}

export default function* root() {
  yield all([
    takeLatest(GET_SUBSCRIPTION_PLAN, getSubscriptionPlanRequest),
    takeLatest(UPDATE_SUBSCRIPTION_PLAN, upgradeSubscriptionPlanRequest),
    takeLatest(PAUSE_SUBSCRIPTION, handlePauseSubscriptionRequest),
    takeLatest(SUBSCRIPTION_FEEDBACK_REQUEST, handleSubscriptionFeedbackRequest),
    takeLatest(CREATE_BILLING_CHECKOUT, createBillingCheckoutRequest),
    takeLatest(APPLY_BILLING_PLAN_CHANGE, applyBillingPlanChangeRequest),
    takeLatest(OPEN_BILLING_MANAGEMENT, openBillingManagementRequest),
    takeLatest(GET_BILLING_PLANS, getBillingPlansRequest),
  ]);
}
